import {inboxService} from "@/services";
import Vuetify from "vuetify";

const vuetify = new Vuetify()

let initialState = {
  isLoadingInboxes: false,
  inboxRetrievalErrors: [],
  inboxes: [], //all
  inboxItems: [],
  userInboxes: [],
  columnizedInboxes: [],
  maxNumberOfColumns: 3,
  maxItemsPerColumn: 10,
  groupedInboxes: [],
  selectedInbox: {
    id: '',
    inboxname: '',
    deletionDeadline: '',
  },
  selectedContext: 'unknown',
  latestSync: '',
  inboxFilters: [],
  clientFilters: [],
  selectedInboxFilters: [],
  selectedClientFilters: [],
  deletionDeadline: {
    min: 5,
    max: 180,
  },
}

export default {
  state: () => initialState,
  mutations: {
    SET_INBOXES(state, inboxes){
      state.inboxes = inboxes
    },
    SET_INBOX_RETRIEVAL_ERRORS(state, errors){
      state.inboxRetrievalErrors = errors
    },
    SET_USER_INBOXES(state, userInboxes) {
      // PRELIMINARY SORT
      state.userInboxes = userInboxes?.sort((a, b) => {
        if (a.inboxname === 'Posteingang' && b.inboxname !== 'Posteingang') return -1
        if (b.inboxname === 'Posteingang' && a.inboxname !== 'Posteingang') return 1
        if (a.inboxname && b.inboxname) {
          return a.inboxname.localeCompare(b.inboxname)
        }
        return 0
      })
    },
    SET_LATEST_SYNC(state, newTimestamp) {
      state.latestSync = newTimestamp
    },
    SET_SELECTED_CONTEXT(state, context) {
      state.selectedContext = context
    },
    SET_SELECTED_INBOX(state, inbox) {
      state.selectedInbox = inbox
    },
    SET_CLIENT_FILTERS(state, clientFilters){
      clientFilters ? state.clientFilters = clientFilters : state.clientFilters = []
    },
    SET_INBOX(state, items) {
      items ? state.inboxItems = items : state.inboxItems = []
    },
    SET_INBOX_FILTERS(state, inboxFilters){
      if (inboxFilters) {
        const sortedData = inboxFilters?.sort((a, b) => {
          if (a.inboxname === 'Posteingang') return -1
          if (b.inboxname === 'Posteingang') return 1
          return a?.inboxname?.localeCompare(b?.inboxname)
        })
        state.inboxFilters = sortedData?.map(item => item?.inboxname)
      } else {
        state.inboxFilters = []
      }
    },
    CALCULATE_MAXIMUM_NUMBER_OF_COLUMNS(state) {
      if (vuetify?.framework?.breakpoint?.xs) return state.maxNumberOfColumns = 1 // Extra small screens
      if (vuetify?.framework?.breakpoint?.sm) return state.maxNumberOfColumns = 2 // Small screens
      if (vuetify?.framework?.breakpoint?.md) return state.maxNumberOfColumns = 3 // Medium screens
      if (vuetify?.framework?.breakpoint?.lg) return state.maxNumberOfColumns = 4 // Large screens
      return state.maxNumberOfColumns = 5 // Extra large screens
    },
    GROUP_INBOXES(state) {
      state.groupedInboxes = [];

      state?.userInboxes?.forEach((inbox) => {
        if (inbox?.inboxname?.includes('_')) {
          const [group, subGroup] = inbox.inboxname.split('_');

          let parentGroup = state.groupedInboxes.find(g => g.inboxname === group);
          if (!parentGroup) {
            parentGroup = {
              inboxname: group,
              id: inbox.id,
              children: [],
              deletionDeadline: inbox.deletionDeadline
            };
            state.groupedInboxes.push(parentGroup);
          }

          if (subGroup) {
            if (!parentGroup.children) {
              parentGroup.children = [];
            }
            parentGroup.children.push({
              inboxname: inbox.inboxname,
              id: inbox.id,
              deletionDeadline: inbox.deletionDeadline
            });
          }
        } else {
          state.groupedInboxes.push({
            inboxname: inbox.inboxname,
            id: inbox.id,
            deletionDeadline: inbox.deletionDeadline
          });
        }
      });
    }
  },
  actions: {
    async fetchUserInboxes({commit, dispatch, state}) {
      state.isLoadingInboxes = true
      const response = await inboxService.getInboxesList()
      state.isLoadingInboxes = false
      commit('SET_USER_INBOXES', response?.data)
      dispatch('columnizeInboxes')
    },
    async fetchInbox({commit, state}){
      state.isLoadingInboxes = true
      const response = await inboxService.getInbox()
      state.isLoadingInboxes = false
      commit('SET_INBOX', response?.data)
    },
    async fetchInboxList({commit, state}){
      state.isLoadingInboxes = true
      const response = await inboxService.getInboxesList()
      state.isLoadingInboxes = true
      commit('SET_INBOXES', response?.data)
    },
    async fetchLatestInboxSync({commit}) {
      const response = await inboxService.getLastImportDate()
      commit('SET_LATEST_SYNC', response?.lastImportDate)
    },
    async fetchClientFilters({commit}) {
      const response = await  inboxService.getClientsToFilterList()
      commit('SET_CLIENT_FILTERS', response?.data)
    },
    async fetchInboxFilters({commit}) {
      const response = await inboxService.getUserInboxesList()
      commit('SET_INBOX_FILTERS', response?.data)
    },
    columnizeInboxes({commit, state}) {
      commit('CALCULATE_MAXIMUM_NUMBER_OF_COLUMNS')
      commit('GROUP_INBOXES')
      const maxItemsPerColumn = +state.maxItemsPerColumn
      const maxNumberOfColumns = +state.maxNumberOfColumns
      const groupedInboxes = state?.groupedInboxes
      const numberOfColumns = Math.min(Math.max(Math.ceil(groupedInboxes?.length / maxItemsPerColumn), 1), maxNumberOfColumns)
      const numberOfRows = Math.ceil(groupedInboxes?.length / numberOfColumns)
      let columns = Array.from({length: numberOfColumns}, () => [])
      for (let col = 0; col < numberOfColumns; col++) {
        for (let row = 0; row < numberOfRows; row++) {
          let index = col * numberOfRows + row
          if (index < groupedInboxes?.length) {
            columns[col].push(groupedInboxes[index])
          }
        }
      }
      return state.columnizedInboxes = columns
    }
  },
  getters: {
    isLoadingInboxes: state => {
      return state.isLoadingInboxes
    },
    inboxRetrievalErrors: state => {
      return state.inboxRetrievalErrors
    },
    inboxes: state => {
      return state.inboxes
    },
    latestSync: state => {
      const optionsDate = {day: '2-digit', month: '2-digit', year: 'numeric'}
      const optionsTime = {hour: '2-digit', minute: '2-digit', hour12: false}
      const date = new Date(state?.latestSync).toLocaleDateString('de-DE', optionsDate)
      const time = new Date(state?.latestSync).toLocaleTimeString('de-DE', optionsTime)
      if (date !== 'Invalid Date') {
        return `${date}, ${time} Uhr`
      }
      return state.latestSync
    },
    userInboxes: state => {
      return state.userInboxes
    },
    clientFilters: state => {
      return state.clientFilters
    },
    inboxFilters: state => {
      return state.inboxFilters
    },
    inboxItems: state => {
      return state.inboxItems
    },
    openItems: state => {
      return state.inboxItems
      // .filter(this.handleClientFilter)
      // .filter(this.handleInboxFilter)
      .filter(item => item.inboxStatus === 1).sort((a, b) => a.id - b.id)
    },
    processingItems: (state, getters, rootState, rootGetters) => {
      return state.inboxItems
      // .filter(this.handleClientFilter)
      // .filter(this.handleInboxFilter)
      .filter(item => {
        return item.inboxStatus === 2 && item.userId == rootGetters['userId']
      })
      .sort((a, b) => a.id - b.id)
    },
    processingItemsOther: (state, getters, rootState, rootGetters) => {
      return state.inboxItems
      // .filter(this.handleClientFilter)
      // .filter(this.handleInboxFilter)
      .filter(item => {
        return item.inboxStatus === 2 && item.userId != rootGetters['userId']
        // return item.inboxStatus === 2 && item.userId != this.userId
      })
      .sort((a, b) => a.id - b.id)
    },
    closedItems: state => {
      return state.inboxItems
      // .filter(this.handleClientFilter)
      // .filter(this.handleInboxFilter)
      .filter(item => item.inboxStatus === 3)
    },
    columnizedInboxes: state => {
      return state.columnizedInboxes
    },
    deletionDeadline: state => {
      return state.deletionDeadline
    },
    selectedInbox: state => {
      return state.selectedInbox
    },
    selectedContext: state => {
      return state.selectedContext
    },
  }
}
